import { useLayoutEffect, useRef, useState } from "react";
import { OverlayHostContext } from "@/design/primitives";
import { FrameCaption } from "./FrameCaption";

const W = 1440;
const H = 900;

const useFrameMetrics = (outerRef, contentRef) => {
  const [m, setM] = useState({ scale: 1, contentH: H });
  useLayoutEffect(() => {
    const measure = () =>
      setM({ scale: Math.min(1, outerRef.current.clientWidth / W), contentH: contentRef.current.offsetHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(outerRef.current);
    ro.observe(contentRef.current);
    measure();
    return () => ro.disconnect();
  }, [outerRef, contentRef]);
  return m;
};

export const DesktopFrame = ({ children, autoHeight = false, testId = "review-frame-desktop" }) => {
  const outerRef = useRef(null);
  const contentRef = useRef(null);
  const [host, setHost] = useState(null);
  const { scale, contentH } = useFrameMetrics(outerRef, contentRef);
  const h = autoHeight ? Math.max(H, contentH) : H;

  return (
    <figure data-testid={testId} className="min-w-0 flex-1">
      <FrameCaption>
        Desktop · 1440 × 900{scale < 1 && " · scaled to fit"}
        {!autoHeight && (
          <span data-testid="desktop-fit-indicator" className="ml-3 normal-case tracking-normal text-os-ink-3">
            {contentH <= H ? "— fits one screen, no scrolling" : "— scrolls"}
          </span>
        )}
      </FrameCaption>
      <div ref={outerRef} className="w-full" style={{ height: h * scale }}>
        <div
          className="os-canvas relative overflow-hidden rounded-[14px] border border-os-border-strong"
          style={{ width: W, height: h, transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
          <OverlayHostContext.Provider value={host}>
            <div className="h-full overflow-y-auto">
              <div ref={contentRef}>{children}</div>
            </div>
          </OverlayHostContext.Provider>
          <div ref={setHost} className="pointer-events-none absolute inset-0 z-50" />
        </div>
      </div>
    </figure>
  );
};
